import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getAnnouncements,
  getBannerAnnouncements,
  getAnnouncement,
  createAnnouncement,
  updateAnnouncement,
  publishAnnouncement,
  archiveAnnouncement,
  deleteAnnouncement,
  dismissAnnouncement,
  getPlatformAnnouncements,
  dismissPlatformAnnouncement,
} from '@/api/services/announcements.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type {
  AnnouncementFilterParams,
  CreateAnnouncementPayload,
  UpdateAnnouncementPayload,
} from '@/types/announcement.types'

export const announcementQueryKeys = {
  all: ['announcements'] as const,
  list: (params?: AnnouncementFilterParams) => ['announcements', 'list', params] as const,
  banner: ['announcements', 'banner'] as const,
  platformBanner: ['announcements', 'platform-banner'] as const,
  detail: (id: string) => ['announcements', 'detail', id] as const,
}

const BANNER_POLL_INTERVAL_MS = 60 * 1000 // 60s

export function useAnnouncements(params?: AnnouncementFilterParams) {
  return useQuery({
    queryKey: announcementQueryKeys.list(params),
    queryFn: () => getAnnouncements(params),
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useBannerAnnouncements() {
  return useQuery({
    queryKey: announcementQueryKeys.banner,
    queryFn: getBannerAnnouncements,
    staleTime: BANNER_POLL_INTERVAL_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: BANNER_POLL_INTERVAL_MS,
  })
}

/**
 * Platform announcements, polled on the same interval as the tenant feed.
 *
 * Its own query rather than a merge inside one: a tenant feed failing should
 * not take an outage notice down with it, and the two dismiss endpoints are
 * different.
 */
export function usePlatformAnnouncements() {
  return useQuery({
    queryKey: announcementQueryKeys.platformBanner,
    queryFn: getPlatformAnnouncements,
    staleTime: BANNER_POLL_INTERVAL_MS,
    gcTime: 5 * 60 * 1000,
    refetchInterval: BANNER_POLL_INTERVAL_MS,
  })
}

export function useDismissPlatformAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: dismissPlatformAnnouncement,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: announcementQueryKeys.platformBanner })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useAnnouncement(id: string | null) {
  return useQuery({
    queryKey: announcementQueryKeys.detail(id ?? ''),
    queryFn: () => getAnnouncement(id!),
    enabled: !!id,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (dto: CreateAnnouncementPayload) => createAnnouncement(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all })
      toast.success('Announcement created')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateAnnouncementPayload }) =>
      updateAnnouncement(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all })
      queryClient.invalidateQueries({
        queryKey: announcementQueryKeys.detail(variables.id),
      })
      toast.success('Announcement updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function usePublishAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => publishAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all })
      toast.success('Announcement published successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useArchiveAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => archiveAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all })
      toast.success('Announcement archived')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteAnnouncement(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all })
      toast.success('Announcement deleted')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useDismissAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => dismissAnnouncement(id),
    onSuccess: () => {
      // Invalidate the whole feature so list views also reflect the dismissal.
      queryClient.invalidateQueries({ queryKey: announcementQueryKeys.all })
    },
    // We don't show a toast for this, it should be silent
  })
}
